/**
 * Person properties sent with PostHog `identify`.
 *
 * `email` comes from the auth user. `name` prefers `profiles.full_name` (what
 * the user sees on their profile) and falls back to the OAuth provider's
 * metadata; email/password sign-ups have no name until they edit the profile,
 * so the key is omitted rather than sent empty.
 */

type AuthUserLike = {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
};

const METADATA_NAME_KEYS = ['full_name', 'fullName', 'name'] as const;

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

export function nameFromMetadata(metadata: AuthUserLike['user_metadata']): string | null {
  if (!metadata) return null;
  for (const key of METADATA_NAME_KEYS) {
    const name = nonEmpty(metadata[key]);
    if (name) return name;
  }
  return null;
}

export function buildIdentifyTraits(
  user: AuthUserLike,
  profileFullName?: string | null,
): Record<string, string> {
  const traits: Record<string, string> = {};
  const email = nonEmpty(user.email);
  if (email) traits.email = email;
  const name = nonEmpty(profileFullName) ?? nameFromMetadata(user.user_metadata);
  if (name) traits.name = name;
  return traits;
}
